import { Link } from '@tanstack/react-router';
import { CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { formatDate } from '~/utils/date';

import type { TrialPlatform } from '../types/trial.types';

/** Maps trial platform keys to human-readable product names. */
const PLATFORM_DISPLAY_NAMES: Record<TrialPlatform, string> = {
  google: 'Google Ads',
  meta: 'Meta Ads',
};

interface TrialNotStartedMessageProps {
  platform: TrialPlatform;
  pocStartDate: string | null;
}

/**
 * Full-page message shown in place of the trial wizard when the platform's
 * TRIAL_POC window hasn't opened yet (`now < pocStartDate`). No catalog data
 * exists yet, so there's nothing to show read-only.
 */
export function TrialNotStartedMessage({ platform, pocStartDate }: TrialNotStartedMessageProps) {
  const { t } = useTranslation('trial');

  return (
    <div
      className="bg-gradient-page flex h-full min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center"
      data-testid="trial-not-started-page"
    >
      <CalendarClock className="text-muted-foreground h-12 w-12" aria-hidden="true" />
      <div className="space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="trial-not-started-title">
          {t('notStarted.title', { platform: PLATFORM_DISPLAY_NAMES[platform] })}
        </h1>
        <p className="text-muted-foreground max-w-md text-sm">
          {t('notStarted.description', {
            startDate: pocStartDate ? formatDate(pocStartDate) : '',
          })}
        </p>
      </div>
      <Button asChild variant="outline" className="cursor-pointer">
        <Link to="/" data-testid="trial-not-started-back-to-dashboard-link">
          {t('notStarted.backToDashboard')}
        </Link>
      </Button>
    </div>
  );
}
